import { isFlowTheme } from 'src/Libs/utils'
import { useUserRoles } from 'src/Hooks/user'
import { Installation } from '../models'
import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'

/**
 * Derive the installation page access mode for UI gating and navigation.
 *
 * This hook computes a high-level `InstallationPageAccess` string used by
 * components to decide which pages, links and actions are available for a
 * given installation. The decision is based on:
 * - whether the installation exists (`installation.id`)
 * - the installation's `installationStatus` and whether an EMS is present
 *   (`installation.emsMasterDeviceId`)
 * - the current theme (`isFlowTheme()`): Flow and E3DC have slightly different
 *   setup/status semantics
 * - the current user's roles from `useUserRoles()` (`isInstaller`,
 *   `isServiceUser`, `isAdmin`)
 *
 * Semantic summary of returned access values:
 * - `full-access`: installation is completed (or in an error state) and either
 *   has an EMS, or the user is a service/admin (support fallback). Components
 *   should treat this as unrestricted access to installation pages.
 * - `flow-setup-configurator-access` / `flow-setup-restricted-access`:
 *   Flow-theme installations in construction (incomplete without EMS). The
 *   `configurator` variant is for installers/service/admins; `restricted` is
 *   the limited view for end-users/partners.
 * - `e3dc-setup-configurator-access` / `e3dc-setup-restricted-access`:
 *   Equivalent modes for the E3DC theme (planned without EMS).
 * - `access-denied`: no installation id or otherwise not permitted; components
 *   should hide navigation and show appropriate empty/forbidden states.
 *
 * Usage example:
 * const access = useInstallationAccessType(installation)
 * if (access === 'full-access') { // show full dashboard }
 *
 * @param installation Optional installation model used to compute access
 * @returns InstallationPageAccess One of the string union values describing
 * the allowed UI behavior for the installation
 */
export const useInstallationAccessType = (installation?: Installation): InstallationPageAccess => {
  const { isInstaller, isServiceUser, isAdmin } = useUserRoles()
  const { pageActions } = useNewRelic()

  if (!installation?.id) return 'access-denied'

  const installationStatus = installation.installationStatus
  const hasEmsId = !!installation.emsMasterDeviceId
  const isAdminOrService = isAdmin || isServiceUser

  // Full access: completed setup or error-state
  const e3dcFullAccess = (installationStatus === 'incomplete' || installationStatus === 'ok') && hasEmsId
  const flowFullAccess = installationStatus === 'ok' && hasEmsId
  const hasFullAccess = (isFlowTheme ? flowFullAccess : e3dcFullAccess) || installationStatus === 'error'

  // Installation construction/setup phase
  const isConstructionPhaseFlow = isFlowTheme && (installationStatus === 'incomplete' || !hasEmsId)
  const isConstructionPhaseE3dc = !isFlowTheme && installationStatus === 'planned'

  if (hasFullAccess) return 'full-access'

  if (isConstructionPhaseFlow) {
    if (isInstaller || isAdminOrService) return 'flow-setup-configurator-access'
    // Default for user and partner
    return 'flow-setup-restricted-access'
  }

  if (isConstructionPhaseE3dc) {
    if (isInstaller || isAdminOrService) return 'e3dc-setup-configurator-access'
    // Default user and partner
    return 'e3dc-setup-restricted-access'
  }

  if (isInstaller) {
    // An edge-case fallback for installers - log to newRelic to keep an eye on installations with unhandled states
    pageActions.logEvent({
      logValue: `useInstallationAccessType. installationId: ${installation.id}, installationStatus: ${installationStatus}, hasEmsId: ${hasEmsId}`,
    })
    return 'full-access'
  }

  // Fallback for service-user → allow access to all pages for support
  return isAdminOrService ? 'full-access' : 'access-denied'
}

export type InstallationPageAccess =
  | 'full-access'
  | 'flow-setup-restricted-access'
  | 'flow-setup-configurator-access'
  | 'e3dc-setup-restricted-access'
  | 'e3dc-setup-configurator-access'
  | 'access-denied'
